import { WorkoutInputs, FuelingRecommendation, TimingNote, PreRaceProtocol, BottleBreakdown, SegmentBreakdown } from '../types';

export function calculateFueling(inputs: WorkoutInputs): FuelingRecommendation {
  // Calculate total duration based on sport
  let totalMinutes = 0;
  let swimMinutes = 0;
  let bikeMinutes = 0;
  let runMinutes = 0;
  
  if (inputs.sport === 'triathlon') {
    swimMinutes = inputs.swimMinutes;
    bikeMinutes = inputs.bikeHours * 60 + inputs.bikeMinutes;
    runMinutes = inputs.runHours * 60 + inputs.runMinutes;
    totalMinutes = swimMinutes + bikeMinutes + runMinutes;
  } else {
    totalMinutes = inputs.durationHours * 60 + inputs.durationMinutes;
  }

  const totalHours = totalMinutes / 60;

  // NEW 4-TIER CARB DOSING LOGIC
  let baseCarbsPerHour = 0;
  let nextLevelCarbs = 0;
  let carbToleranceNote = '';

  // Base rate by tier (scoops/hr)
  if (inputs.carbTolerance === 'building') {
    baseCarbsPerHour = 30; // 1 scoop/hr
    nextLevelCarbs = 60;
    carbToleranceNote = 'Work up over 4–6 weeks — never jump levels mid-race.';
  } else if (inputs.carbTolerance === 'intermediate') {
    baseCarbsPerHour = 60; // 2 scoops/hr
    nextLevelCarbs = 90;
    carbToleranceNote = 'Work up over 4–6 weeks — never jump levels mid-race.';
  } else if (inputs.carbTolerance === 'performance') {
    baseCarbsPerHour = 90; // 3 scoops/hr
  } else if (inputs.carbTolerance === 'elite') {
    baseCarbsPerHour = 90; // 3 scoops/hr base, can go to 120g
  }

  // Apply modifiers
  let carbsPerHour = baseCarbsPerHour;

  // Under 60 min: 0 carbs
  if (totalMinutes < 60) {
    carbsPerHour = 0;
  }
  // 60-90 min: reduce by 1 scoop/hr, min 1 scoop/hr
  else if (totalMinutes >= 60 && totalMinutes <= 90) {
    carbsPerHour = Math.max(30, baseCarbsPerHour - 30);
  }
  // Easy intensity: reduce by 1 scoop/hr, min 1/hr over 90 min
  else if (inputs.intensity === 'easy' && totalMinutes > 90) {
    carbsPerHour = Math.max(30, baseCarbsPerHour - 30);
  }
  // Elite over 3hrs at Moderate/Hard: allow 4 scoops/hr (120g)
  else if (inputs.carbTolerance === 'elite' && totalHours > 3 && (inputs.intensity === 'moderate' || inputs.intensity === 'hard')) {
    carbsPerHour = 120; // 4 scoops/hr
  }

  // Calculate triathlon segment breakdown
  let segmentBreakdown: SegmentBreakdown | undefined;
  let totalCarbs = 0;

  if (inputs.sport === 'triathlon') {
    const bikeCarbsPerHour = carbsPerHour;
    const runCarbsPerHour = Math.round(carbsPerHour * 0.6); // Reduce carbs on run
    
    const bikeCarbs = (bikeMinutes / 60) * bikeCarbsPerHour;
    const runCarbs = (runMinutes / 60) * runCarbsPerHour;
    totalCarbs = bikeCarbs + runCarbs;

    segmentBreakdown = {
      swim: {
        duration: `${swimMinutes}min`,
        fueling: '15g carbs + 1 electrolyte serving 15min before swim start'
      },
      bike: {
        duration: `${Math.floor(bikeMinutes / 60)}h ${bikeMinutes % 60}min`,
        carbsPerHour: bikeCarbsPerHour,
        totalCarbs: Math.round(bikeCarbs),
        electrolytes: inputs.heat === 'hot' ? '1 serving per hour' : '1 serving per 90min'
      },
      run: {
        duration: `${Math.floor(runMinutes / 60)}h ${runMinutes % 60}min`,
        carbsPerHour: runCarbsPerHour,
        totalCarbs: Math.round(runCarbs),
        electrolytes: inputs.heat === 'hot' ? '1 serving per 45min' : '1 serving per hour'
      }
    };
  } else {
    totalCarbs = carbsPerHour * totalHours;
  }

  const carbScoops = Math.round(totalCarbs / 30);

  // NEW ELECTROLYTE DOSING LOGIC
  let electrolyteServingsPerHour = 1;
  
  if (inputs.heat === 'hot') {
    electrolyteServingsPerHour = inputs.highSweatRate ? 2 : 1.5;
  } else if (inputs.heat === 'moderate') {
    electrolyteServingsPerHour = inputs.highSweatRate ? 1.5 : 1;
  } else { // cool
    electrolyteServingsPerHour = inputs.highSweatRate ? 1.5 : 1;
  }

  // Cap at 2 servings/hr max
  electrolyteServingsPerHour = Math.min(2, electrolyteServingsPerHour);
  
  const totalElectrolyteServings = Math.ceil(electrolyteServingsPerHour * totalHours);
  const totalSodium = totalElectrolyteServings * 800;

  // Caffeine calculation
  let caffeineMg = 0;
  let caffeineScoops = 0;
  if (inputs.caffeinePreference !== 'none' && totalMinutes >= 60) {
    const caffeinePerHour = inputs.caffeinePreference === 'high' ? 60 : 30;
    caffeineMg = Math.min(caffeinePerHour * totalHours, 300);
    caffeineScoops = Math.round(caffeineMg / 30);
  }
  const lTheanineMg = caffeineMg * 2;

  // Generate strategy label
  let workoutLabel = '';
  if (inputs.sport === 'triathlon') {
    if (totalHours >= 8) {
      workoutLabel = `Ironman-Distance Triathlon — ${Math.floor(totalHours)}h ${Math.round((totalHours % 1) * 60)}min Total`;
    } else if (totalHours >= 4) {
      workoutLabel = `Half-Ironman Triathlon — ${Math.floor(totalHours)}h ${Math.round((totalHours % 1) * 60)}min Total`;
    } else {
      workoutLabel = `Sprint/Olympic Triathlon — ${Math.floor(totalHours)}h ${Math.round((totalHours % 1) * 60)}min Total`;
    }
  } else {
    const sportName = inputs.sport.charAt(0).toUpperCase() + inputs.sport.slice(1);
    workoutLabel = `${Math.floor(totalHours)}h ${Math.round((totalHours % 1) * 60)}min ${sportName}`;
  }

  if (inputs.isRaceDay) {
    workoutLabel += ' — Race Day';
  }

  // Combine label
  const components = [];
  if (carbsPerHour > 0) components.push('Carbs');
  components.push('Electrolytes');
  if (caffeineMg > 0) components.push('Caffeine');
  const combineLabel = components.join(' + ');

  // Rationale
  const rationale: string[] = [];
  
  if (totalMinutes < 60) {
    rationale.push('No carbs needed under 60 min.');
  } else if (inputs.sport === 'triathlon') {
    rationale.push(`Triathlon fueling is segment-specific. Swim: pre-race carbs only. Bike: primary fueling window at ${segmentBreakdown!.bike.carbsPerHour}g/hr. Run: reduced to ${segmentBreakdown!.run.carbsPerHour}g/hr due to GI stress.`);
  } else if (carbsPerHour > 0) {
    rationale.push(`${carbsPerHour}g/hr carbs using 1:0.8 maltodextrin:fructose ratio for optimal absorption.`);
  }

  if (inputs.carbTolerance === 'building' || inputs.carbTolerance === 'intermediate') {
    rationale.push(`Starting at ${carbsPerHour}g/hr. Training your gut to absorb more carbs takes 4-8 weeks of consistent fueling.`);
  } else if (inputs.carbTolerance === 'elite') {
    rationale.push(`Elite-level fueling at ${carbsPerHour}g/hr. This requires months of gut training.`);
  }

  rationale.push(`${Math.round(electrolyteServingsPerHour * 800)}mg sodium per hour accounts for ${inputs.heat} conditions${inputs.highSweatRate ? ' and high sweat rate' : ''}.`);
  
  if (caffeineMg > 0) {
    rationale.push(`${caffeineMg}mg total caffeine paired with ${lTheanineMg}mg L-Theanine for smooth focus without jitters.`);
  }

  if (inputs.isRaceDay && totalHours >= 8) {
    rationale.push('Ironman-distance protocol: higher carb rate, consistent sodium, caffeine timed for final hours.');
  }

  // Timing notes
  const timing: TimingNote[] = generateTiming(inputs, totalMinutes, carbsPerHour, caffeineScoops);

  // Pre-race protocol
  let preRace: PreRaceProtocol | undefined;
  if (inputs.isRaceDay) {
    preRace = {
      nightBefore: 'Carb-load dinner: 8-10g carbs per kg bodyweight. Focus on familiar foods — white rice, pasta, potatoes. Avoid fiber and fat. Hydrate with electrolytes.',
      raceMorning: 'Eat 3-4 hours before start: 2-3g carbs per kg bodyweight. Examples: white bagel with honey, white rice with banana, oatmeal with maple syrup. Sip electrolytes until 1 hour before start.',
      warmup: '30-60 minutes before start: 15-30g carbs + 1 electrolyte serving. This primes your system and tops off glycogen without causing GI distress.'
    };
  }

  // Bottle breakdown
  let bottleBreakdown: BottleBreakdown[] | undefined;
  if (inputs.bottles.length > 0) {
    bottleBreakdown = generateBottleBreakdown(inputs, carbScoops, totalElectrolyteServings, caffeineScoops, totalMinutes);
  }

  return {
    totalMinutes,
    carbsPerHour,
    totalCarbs: Math.round(totalCarbs),
    carbScoops,
    sodiumMg: Math.round(totalSodium),
    electrolyteScoops: totalElectrolyteServings,
    caffeineMg,
    caffeineScoops,
    lTheanineMg,
    strategy: combineLabel,
    rationale,
    workoutLabel,
    combineLabel,
    timing,
    preRace,
    bottleBreakdown,
    carbToleranceNote: (inputs.carbTolerance === 'building' || inputs.carbTolerance === 'intermediate') ? carbToleranceNote : undefined,
    nextLevelCarbs: (inputs.carbTolerance === 'building' || inputs.carbTolerance === 'intermediate') ? nextLevelCarbs : undefined,
    segmentBreakdown
  };
}

function generateTiming(inputs: WorkoutInputs, totalMinutes: number, carbsPerHour: number, caffeineScoops: number): TimingNote[] {
  const timing: TimingNote[] = [];

  if (inputs.sport === 'triathlon') {
    timing.push({
      time: '15min before swim',
      note: '15-30g carbs + 1 electrolyte serving. Light fueling to prime your system.'
    });

    timing.push({
      time: 'T1 (Swim to Bike)',
      note: 'Begin bike fueling immediately. First bottle should be ready on your bike.'
    });

    const bikeMinutes = inputs.bikeHours * 60 + inputs.bikeMinutes;
    timing.push({
      time: 'Bike segment',
      note: `Fuel every 15-20 minutes. Target ${carbsPerHour}g carbs per hour. This is your primary fueling window.`
    });

    if (caffeineScoops > 0 && bikeMinutes > 60) {
      timing.push({
        time: `Bike: ${Math.floor(bikeMinutes / 2)}min mark`,
        note: 'Begin caffeine dosing. Timed for second half of bike and full run.'
      });
    }

    timing.push({
      time: 'T2 (Bike to Run)',
      note: 'Grab run nutrition. Reduce carb intake — your gut is more stressed running.'
    });

    timing.push({
      time: 'Run segment',
      note: 'Smaller, more frequent sips. Electrolytes are critical. Carbs at 60% of bike rate.'
    });

  } else {
    if (totalMinutes >= 60) {
      timing.push({
        time: 'Start',
        note: 'Begin fueling within first 15-20 minutes. Don\'t wait until you\'re depleted.'
      });
    }

    if (carbsPerHour > 0) {
      timing.push({
        time: 'Every 15-20min',
        note: `Sip consistently to hit ${carbsPerHour}g carbs per hour. Small frequent doses beat large boluses.`
      });
    }

    if (totalMinutes >= 90) {
      timing.push({
        time: `${Math.floor(totalMinutes / 2)}min`,
        note: 'Halfway point. Check hydration and fueling pace. Adjust if needed.'
      });
    }

    if (caffeineScoops > 0 && totalMinutes >= 120) {
      const caffeineStart = Math.floor(totalMinutes * 0.6);
      timing.push({
        time: `${caffeineStart}min`,
        note: 'Begin caffeine. Timed for final third when focus matters most.'
      });
    }

    if (totalMinutes >= 60) {
      timing.push({
        time: 'Final 30min',
        note: 'Maintain fueling through the finish. Don\'t back off early.'
      });
    }
  }

  return timing;
}

function generateBottleBreakdown(
  inputs: WorkoutInputs,
  totalCarbScoops: number,
  totalElectrolyteServings: number,
  totalCaffeineScoops: number,
  totalMinutes: number
): BottleBreakdown[] {
  const breakdown: BottleBreakdown[] = [];
  const numBottles = inputs.bottles.length;

  // Max safe concentration per bottle
  const maxScoopsPerBottle: Record<string, number> = {
    '250ml': 3,
    '500ml': 3,
    '750ml': 4,
    '1.5L': 5,
    '3L': 8
  };

  // Distribute carb scoops EVENLY across bottles
  const baseCarbsPerBottle = Math.floor(totalCarbScoops / numBottles);
  let remainingCarbScoops = totalCarbScoops - (baseCarbsPerBottle * numBottles);

  // Distribute electrolytes evenly
  const baseElectrolytesPerBottle = Math.floor(totalElectrolyteServings / numBottles);
  let remainingElectrolytes = totalElectrolyteServings - (baseElectrolytesPerBottle * numBottles);

  inputs.bottles.forEach((bottle, index) => {
    const isSecondHalf = index >= Math.floor(numBottles / 2);
    const isLastBottle = index === numBottles - 1;

    // Distribute carbs - extra scoops go in Bottle 1 or 2, NEVER last bottle
    let carbScoops = baseCarbsPerBottle;
    if (remainingCarbScoops > 0 && index < 2 && !isLastBottle) {
      carbScoops += 1;
      remainingCarbScoops -= 1;
    }

    // Distribute electrolytes
    let electrolyteScoops = baseElectrolytesPerBottle;
    if (remainingElectrolytes > 0) {
      electrolyteScoops += 1;
      remainingElectrolytes -= 1;
    }

    // Put caffeine ONLY in second half bottles, NEVER Bottle 1
    let caffeineScoops = 0;
    if (totalCaffeineScoops > 0 && isSecondHalf && index > 0) {
      const caffeineBottles = Math.ceil(numBottles / 2);
      caffeineScoops = Math.floor(totalCaffeineScoops / caffeineBottles);
    }

    // Check max concentration warning
    const totalScoopsInBottle = carbScoops + electrolyteScoops + caffeineScoops;
    const maxAllowed = maxScoopsPerBottle[bottle.size] || 3;
    const concentrationWarning = totalScoopsInBottle > maxAllowed;

    // Calculate start timing
    const bottleStartMin = Math.floor((totalMinutes / numBottles) * index);
    const hours = Math.floor(bottleStartMin / 60);
    const mins = bottleStartMin % 60;
    let startTiming = '';
    
    if (index === 0) {
      startTiming = inputs.sport === 'triathlon' ? 'Start on bike segment' : 'Start at beginning';
    } else {
      startTiming = hours > 0 ? `After ${hours}h ${mins}min` : `After ${mins}min`;
    }

    // Generate notes
    const notes: string[] = [];
    
    if (bottle.size === '250ml') {
      notes.push('Take 2-3 sips every 15 minutes.');
    }

    if (caffeineScoops > 0) {
      notes.push('Timed for your second half — when focus matters most.');
    }

    if (index >= 2) {
      notes.push('Consider a different electrolyte flavor to fight flavor fatigue.');
    }

    if (concentrationWarning) {
      notes.push(`⚠️ Concentration exceeds ${maxAllowed} scoops for ${bottle.size}. Consider splitting across more bottles.`);
    }

    if (inputs.sport === 'triathlon' && index === 0) {
      notes.push('Mount this bottle on your bike. Begin fueling immediately after T1.');
    }

    // Mix instructions
    const mixParts: string[] = [];
    if (carbScoops > 0) mixParts.push(`${carbScoops} scoop${carbScoops > 1 ? 's' : ''} carbs`);
    if (electrolyteScoops > 0) mixParts.push(`${electrolyteScoops} serving${electrolyteScoops > 1 ? 's' : ''} electrolytes`);
    if (caffeineScoops > 0) mixParts.push(`${caffeineScoops} scoop${caffeineScoops > 1 ? 's' : ''} caffeine`);
    
    const mixInstructions = `Add ${mixParts.join(' + ')} to ${bottle.size} water.`;
    notes.unshift(mixInstructions);

    breakdown.push({
      bottleLabel: bottle.label,
      bottleSize: bottle.size,
      carbScoops,
      electrolyteScoops,
      caffeineScoops,
      startTiming,
      notes
    });
  });

  return breakdown;
}